import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { customFieldsListLoadable } from '../../../../services/state/AdminPanel/CustomFieldsStateJ';
import { kpiConfigLoadable } from '../../../../services/state/KPIConfigState';
export interface FieldOption {
  id: number | string;
  displayName: string;
  type: 'entity-field' | 'custom-field' | 'kpi';
  dataType?: string; // For showing array indicators
}

export function useFieldOptions() {
  const customFieldsLoadable = useAtomValue(customFieldsListLoadable);
  const kpisLoadable = useAtomValue(kpiConfigLoadable);

  // Handle error states
  if (customFieldsLoadable.state === 'hasError') {
    console.error('Failed to load custom fields:', customFieldsLoadable.error);
  }
  if (kpisLoadable.state === 'hasError') {
    console.error('Failed to load KPIs:', kpisLoadable.error);
  }

  const customFields = customFieldsLoadable.state === 'hasData' ? customFieldsLoadable.data : undefined;
  const kpis = kpisLoadable.state === 'hasData' ? kpisLoadable.data : undefined;

  // Entity fields for company (common fields that can be mapped)
  const companyEntityFields = useMemo<FieldOption[]>(
    () => [
      { id: 'name', displayName: 'Company Name', type: 'entity-field' },
      { id: 'legalName', displayName: 'Legal Name', type: 'entity-field' },
      { id: 'commonName', displayName: 'Common Name', type: 'entity-field' },
      { id: 'website', displayName: 'Website', type: 'entity-field' },
      { id: 'shortDescription', displayName: 'Short Description', type: 'entity-field' },
      { id: 'longDescription', displayName: 'Long Description', type: 'entity-field' },
      { id: 'sector', displayName: 'Sector', type: 'entity-field' },
      { id: 'ceoName', displayName: 'CEO Name', type: 'entity-field' },
      { id: 'foundedDate', displayName: 'Founded Date', type: 'entity-field' },
      { id: 'fullTimeEmployees', displayName: 'Full Time Employees', type: 'entity-field' },
      { id: 'city', displayName: 'City', type: 'entity-field' },
      { id: 'state', displayName: 'State', type: 'entity-field' },
      { id: 'country', displayName: 'Country', type: 'entity-field' },
      { id: 'region', displayName: 'Region', type: 'entity-field' },
      { id: 'zip', displayName: 'Zip Code', type: 'entity-field' },
      { id: 'streetAddress', displayName: 'Street Address', type: 'entity-field' },
      { id: 'logoUrl', displayName: 'Logo URL', type: 'entity-field' },
      { id: 'defaultCurrency', displayName: 'Default Currency', type: 'entity-field' },
      { id: 'fmvBasis', displayName: 'FMV Basis', type: 'entity-field' },
      { id: 'googleDriveFolder', displayName: 'Google Drive Folder', type: 'entity-field' },
      { id: 'dropboxFolder', displayName: 'Dropbox Folder', type: 'entity-field' },
    ],
    []
  );

  // Entity fields for round
  const roundEntityFields = useMemo<FieldOption[]>(
    () => [
      { id: 'name', displayName: 'Round Name', type: 'entity-field' },
      { id: 'round', displayName: 'Round Stage', type: 'entity-field' },
      { id: 'roundDate', displayName: 'Round Date', type: 'entity-field' },
      { id: 'investmentType', displayName: 'Investment Type', type: 'entity-field' },
      { id: 'investmentCode', displayName: 'Investment Code', type: 'entity-field' },
      { id: 'preMoneyValuation', displayName: 'Pre-Money Valuation', type: 'entity-field' },
      { id: 'postMoneyValuation', displayName: 'Post-Money Valuation', type: 'entity-field' },
      { id: 'raiseAmount', displayName: 'Raise Amount', type: 'entity-field' },
      { id: 'fullyDilutedShares', displayName: 'Fully Diluted Shares', type: 'entity-field' },
      { id: 'leadInvestor', displayName: 'Lead Investor', type: 'entity-field' },
      { id: 'leadInvestors', displayName: 'Lead Investors', type: 'entity-field', dataType: 'stringArray' },
      { id: 'coInvestors', displayName: 'Co-Investors', type: 'entity-field', dataType: 'stringArray' },
      { id: 'valuationStepUp', displayName: 'Valuation Step Up', type: 'entity-field' },
      { id: 'initialQSBSQualification', displayName: 'Initial QSBS Qualification', type: 'entity-field' },
    ],
    []
  );

  const customFieldOptions = useMemo<FieldOption[]>(() => {
    if (!customFields) {
      return [];
    }
    return customFields.map((field) => ({
      id: field.id,
      displayName: field.displayName,
      type: 'custom-field' as const,
      dataType: field.type, // Include the data type for array indicators
    }));
  }, [customFields]);

  const kpiOptions = useMemo<FieldOption[]>(() => {
    if (!kpis) {
      return [];
    }
    return kpis.map((kpi) => ({
      id: kpi.id,
      displayName: kpi.displayName,
      type: 'kpi' as const,
    }));
  }, [kpis]);

  const hasCustomFieldsError = customFieldsLoadable.state === 'hasError';
  const hasKpisError = kpisLoadable.state === 'hasError';

  return {
    companyEntityFields,
    roundEntityFields,
    customFields: customFieldOptions,
    kpis: kpiOptions,
    isLoading: customFieldsLoadable.state === 'loading' || kpisLoadable.state === 'loading',
    hasError: hasCustomFieldsError || hasKpisError,
    errorMessage: hasCustomFieldsError
      ? 'Failed to load custom fields. You may not have permission to view custom fields.'
      : hasKpisError
        ? 'Failed to load KPIs. You may not have permission to view KPIs.'
        : null,
  };
}
